import { FC, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { routes } from "src/app/routes";
import { Color, FontSize, FontWeight } from "src/theme";
import { useTranslation } from "src/translations";
import { OutageDto, OutageType } from "src/types";
import { isNonNullish } from "src/utils";
import { OUTAGE_STATE_TEXT } from "../constants";
import { InfoRow } from "../InfoRow";
import { Flex } from "../Layouts";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

type OutageAlertProps = Readonly<{
    outage: OutageDto;
}>;

type AlertProps = Readonly<{
    clickable?: boolean;
    type?: OutageType;
    color?: Color;
}>;

const TooltipContent: FC<{ outage: OutageDto; maxHeight?: number }> = ({ outage, maxHeight }) => {
    const { t } = useTranslation();
    const { start, expectedEnd } = outage;

    return (
        <StyledTooltipWrapper flexDirection="column" gap="0.5rem" $maxHeight={maxHeight}>
            <Flex flexDirection="row" gap="1rem">
                <Alert type={outage.category} />
                <Text weight={FontWeight._700} size={FontSize._14}>
                    {outage.summary}
                </Text>
            </Flex>
            <Flex flexDirection="column" gap="0.25rem">
                <InfoRow title={t("outage.start")} data={start} />
                <InfoRow title={t("outage.expectedEnd")} data={expectedEnd} />
                <InfoRow title={t("outage.state")} data={t(OUTAGE_STATE_TEXT[outage.issueState])} />
                {isNonNullish(outage.subsystemsNames) && (
                    <Flex flexDirection="column">
                        <Text wordBreak="normal" size={FontSize._14}>
                            {t("outage.subSystems")}:
                        </Text>
                        <Text size={FontSize._14} weight={FontWeight._700}>
                            {outage.subsystemsNames.join(", ")}
                        </Text>
                    </Flex>
                )}
                <Flex flexDirection="column">
                    <Text wordBreak="normal" size={FontSize._14}>
                        {t("outage.title")}:
                    </Text>
                    <Text size={FontSize._14} weight={FontWeight._700}>
                        {outage.description}
                    </Text>
                </Flex>
            </Flex>
        </StyledTooltipWrapper>
    );
};

export const Alert: FC<AlertProps> = ({ clickable = false, type, color }) => (
    <OutageAlertWrapper clickable={clickable} type={type} color={color} />
);

// Space kept free between the tooltip and the viewport edge (tooltip gutter, body padding, border)
const TOOLTIP_VIEWPORT_OFFSET = 40;
// Opens downwards unless there is less space than this below the alert
const TOOLTIP_MIN_HEIGHT = 250;
// Lets the user move the pointer from the alert into the tooltip to scroll it
const TOOLTIP_CLOSE_DELAY = 200;

type TooltipPlacement = Readonly<{
    position: "top" | "bottom";
    maxHeight?: number;
}>;

const getTooltipPlacement = (alert: Element): TooltipPlacement => {
    const { top, bottom } = alert.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom - TOOLTIP_VIEWPORT_OFFSET;

    if (spaceBelow >= TOOLTIP_MIN_HEIGHT) {
        return { position: "bottom", maxHeight: spaceBelow };
    }

    return { position: "top", maxHeight: top - TOOLTIP_VIEWPORT_OFFSET };
};

export const OutageAlert: FC<OutageAlertProps> = ({ outage }) => {
    const [placement, setPlacement] = useState<TooltipPlacement>({ position: "bottom" });

    return (
        <Tooltip
            content={<TooltipContent outage={outage} maxHeight={placement.maxHeight} />}
            position={placement.position}
            allowPointerEvents
            closeDelay={TOOLTIP_CLOSE_DELAY}
        >
            <Link
                to={routes.outageDetail.fillPathParams({ outageId: outage.id!.toString() })}
                onPointerEnter={(event) => setPlacement(getTooltipPlacement(event.currentTarget))}
            >
                <Alert clickable type={outage.category} />
            </Link>
        </Tooltip>
    );
};

const OutageAlertWrapper = styled.div<AlertProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: ${({ theme }) => theme.borderRadius._5};
    background-color: ${({ theme, type, color }) => {
        if (color) {
            return color;
        }

        switch (type) {
            case OutageType.PlannedOutage:
                return theme.color.green;
            case OutageType.UnplannedOutage:
                return theme.color.red100;
            case OutageType.UnplannedPartialOutage:
                return theme.color.yellow100;
            default:
                return "unset";
        }
    }};
    cursor: ${({ clickable }) => (clickable ? "pointer" : "unset")};
`;

const StyledTooltipWrapper = styled(Flex)<{ $maxHeight?: number }>`
    padding: 0.5rem;
    width: 270px;
    max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : "none")};
    overflow-y: auto;
    overscroll-behavior: contain;
    white-space: normal;
    word-break: break-word;
`;
